import React, { useState, useEffect } from "react";
import { View, FlatList, StyleSheet, Dimensions, Platform } from "react-native";
import Placeholder from "../general/Placeholder/Placeholder";
import { useGenerateKeywordsMutation } from "../../graphql/generated/graphql";
import Loader from "../common/status/Loader/Loader";
import { isLoading } from "expo-font";
import { announceForAccessibility } from "../../utils/accessibility";
import Sentry from "../../utils/sentry";
import YoutubeVideoPlayer from "./YoutubeVideoPlayer";

const YOUTUBE_API_KEY = "AIzaSyA8Luh5WbUdXzxY6JvmsNfFXdpLUQaZO_c";
const YOUTUBE_SEARCH_URL = `https://www.googleapis.com/youtube/v3/search?part=snippet&maxResults=5&type=video&videoCategoryId=27&videoDuration=medium&regionCode=US&key=${YOUTUBE_API_KEY}`;

const extractTitleAndDescription = (keyword) => {
  const match = /Title: (.+?) \| Description: (.*)/.exec(keyword);
  return {
    title: match ? match[1] : "",
    description: match ? match[2] : "",
  };
};

const YoutubeSearch = ({ isMobile, keyword }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialFetchCompleted, setInitialFetchCompleted] = useState(false);
  const [generateKeywordsMutation] = useGenerateKeywordsMutation();

  useEffect(() => {
    if (Platform.OS === "ios" && !isLoading) {
      announceForAccessibility({ message: "Page is loaded", queue: false });
    }
  }, [isLoading]);

  const cleanKeyword = keyword
    .replace(/<[^>]+>/g, "")
    .split(".")
    .map((str) => str.trim())
    .join(" ");

  const { title, description } = extractTitleAndDescription(cleanKeyword);

  useEffect(() => {
    const fetchVideos = async (title, description) => {
      setLoading(true);
      try {
        const { data } = await generateKeywordsMutation({
          variables: {
            title: title,
            description: description,
          },
        });

        const generatedKeywords = data.generateYoutubeKeywords;
        fetch(`${YOUTUBE_SEARCH_URL}&q=${encodeURIComponent(generatedKeywords)}`)
          .then((response) => response.json())
          .then((data) => {
            const videoResults = data.items.map((item) => ({
              id: item.id.videoId,
              title: item.snippet.title,
              description: item.snippet.description,
            }));

            setVideos(videoResults);
          })
          .catch((error) => {
            Sentry.captureException(error);
          })
          .finally(() => {
            setLoading(false);
            setInitialFetchCompleted(true);
          });
      } catch (error) {
        Sentry.captureException(error);
        setLoading(false);
        setInitialFetchCompleted(true);
      }
    };

    if (cleanKeyword) {
      fetchVideos(title, description);
    }
  }, [title, description]);

  return (
    <View style={{ flex: 1 }}>
      {loading ? (
        <View accessible={true} role="alert" aria-live="assertive" style={styles.loaderContainer}>
          <Loader />
        </View>
      ) : !loading && initialFetchCompleted && videos.length === 0 ? (
        <Placeholder
          accessible={!loading}
          title="No Videos to See Here"
          subtitle="We are past our current capacity for videos, please check back here soon"
        />
      ) : (
        <FlatList
          contentContainerStyle={styles.list}
          data={videos}
          role="grid"
          numColumns={isMobile ? 1 : 2}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => <YoutubeVideoPlayer id={item.id} />}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  list: {
    gap: 20,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default YoutubeSearch;
